/**
 * Maps frequency codes to human-readable Norwegian and English strings
 */
export class FrequencyFormatter {
  private static readonly DEFAULT_PLACEHOLDER = '-';
  private static readonly DEFAULT_LOCALE = 'nb';
  private static readonly ENGLISH_LOCALE_PREFIX = 'en';
  private static readonly CRON_MIN_PARTS = 5;
  private static readonly CRON_MAX_PARTS = 6;
  private static readonly BIWEEKLY_DAYS = 14;

  private static readonly frequencyMap: Record<string, { no: string; en: string }> = {
    // Common frequency patterns
    everyOtherWeek: { no: 'Annenhver uke', en: 'Every other week' },
    everyThirdWeek: { no: 'Hver tredje uke', en: 'Every third week' },
    weekly: { no: 'Ukentlig', en: 'Weekly' },
    biweekly: { no: 'Annenhver uke', en: 'Biweekly' },
    monthly: { no: 'Månedlig', en: 'Monthly' },
    daily: { no: 'Daglig', en: 'Daily' },
    everyTwoWeeks: { no: 'Annenhver uke', en: 'Every two weeks' },
    everyWeek: { no: 'Ukentlig', en: 'Every week' },
    everyMonth: { no: 'Månedlig', en: 'Every month' },
    quarterly: { no: 'Kvartalsvis', en: 'Quarterly' },

    // Norwegian keywords
    daglig: { no: 'Daglig', en: 'Daily' },
    ukentlig: { no: 'Ukentlig', en: 'Weekly' },
    'annenhver uke': { no: 'Annenhver uke', en: 'Every other week' },
    'hver tredje uke': { no: 'Hver tredje uke', en: 'Every third week' },
    månedlig: { no: 'Månedlig', en: 'Monthly' },
    kvartalsvis: { no: 'Kvartalsvis', en: 'Quarterly' },
  };

  /**
   * Formats a frequency string to a human-readable format
   * @param frequency The frequency code or cron expression
   * @param locale The locale to use ('nb' or 'en', defaults to 'nb')
   * @returns Formatted frequency string
   */
  static format(frequency: string, locale: string = FrequencyFormatter.DEFAULT_LOCALE): string {
    if (!frequency) {
      return FrequencyFormatter.DEFAULT_PLACEHOLDER;
    }

    const normalizedFrequency = frequency.toLowerCase().trim();
    const language = this.getLanguageFromLocale(locale);

    return (
      this.findExactMatch(normalizedFrequency, language) ||
      this.findPartialMatch(normalizedFrequency, language) ||
      this.parseCronIfApplicable(frequency, language) ||
      frequency
    );
  }

  private static getLanguageFromLocale(locale: string): 'no' | 'en' {
    return locale.startsWith(FrequencyFormatter.ENGLISH_LOCALE_PREFIX) ? 'en' : 'no';
  }

  private static findExactMatch(normalizedFrequency: string, language: 'no' | 'en'): string | null {
    const match = this.frequencyMap[normalizedFrequency];
    return match ? match[language] : null;
  }

  private static findPartialMatch(normalizedFrequency: string, language: 'no' | 'en'): string | null {
    for (const [key, value] of Object.entries(this.frequencyMap)) {
      if (normalizedFrequency.includes(key.toLowerCase())) {
        return value[language];
      }
    }
    return null;
  }

  private static parseCronIfApplicable(frequency: string, language: 'no' | 'en'): string | null {
    return this.isCronExpression(frequency) ? this.parseCronExpression(frequency, language) : null;
  }

  /**
   * Checks if a string looks like a cron expression
   */
  private static isCronExpression(str: string): boolean {
    const parts = str.trim().split(/\s+/);
    return parts.length >= FrequencyFormatter.CRON_MIN_PARTS &&
           parts.length <= FrequencyFormatter.CRON_MAX_PARTS;
  }

  /**
   * Attempts to parse a cron expression into human-readable format
   */
  private static parseCronExpression(cron: string, lang: 'no' | 'en'): string {
    const parts = cron.trim().split(/\s+/);

    if (parts.length < FrequencyFormatter.CRON_MIN_PARTS) {
      return cron;
    }

    const [, , dayOfMonth, month, dayOfWeek] = parts;

    if (this.isDaily(dayOfMonth, month, dayOfWeek)) {
      return lang === 'no' ? 'Daglig' : 'Daily';
    }

    if (this.isWeekly(dayOfMonth, month, dayOfWeek)) {
      return lang === 'no' ? 'Ukentlig' : 'Weekly';
    }

    if (this.isMonthly(dayOfMonth, month)) {
      return lang === 'no' ? 'Månedlig' : 'Monthly';
    }

    if (dayOfMonth.startsWith('*/')) {
      return this.parseIntervalDays(dayOfMonth, lang);
    }

    return cron;
  }

  private static isDaily(dayOfMonth: string, month: string, dayOfWeek: string): boolean {
    return dayOfMonth === '*' && month === '*' && dayOfWeek === '*';
  }

  private static isWeekly(dayOfMonth: string, month: string, dayOfWeek: string): boolean {
    return dayOfMonth === '*' && month === '*' && dayOfWeek !== '*';
  }

  private static isMonthly(dayOfMonth: string, month: string): boolean {
    return dayOfMonth !== '*' && month === '*';
  }

  private static parseIntervalDays(dayOfMonth: string, lang: 'no' | 'en'): string {
    const days = parseInt(dayOfMonth.substring(2));

    if (days === FrequencyFormatter.BIWEEKLY_DAYS) {
      return lang === 'no' ? 'Annenhver uke' : 'Every other week';
    }

    return lang === 'no' ? `Hver ${days}. dag` : `Every ${days} days`;
  }
}
